import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Plus, Search } from "lucide-react";

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", location: "" });
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get(`/crm/clients`, { params: { q: q || undefined, limit: 200 } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      const { data } = await api.post("/crm/clients", form);
      toast.success("Client added"); setShowNew(false);
      nav(`/app/crm/clients/${data.id}`);
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
  };

  return (
    <div className="p-6" data-testid="clients-page">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0B0D17]">Clients</h1>
          <p className="text-sm text-[#4A4D57]">All your customers in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#747680]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" className="h-9 pl-8 pr-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="clients-search" />
          </div>
          <button onClick={() => setShowNew(true)} className="vp-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="new-client-btn">
            <Plus className="w-4 h-4" /> New client
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-[#E4E5E9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F7] text-[#4A4D57] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Location</th><th className="text-left p-3">Status</th>
              <th className="text-left p-3">Since</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} onClick={() => nav(`/app/crm/clients/${c.id}`)} className="border-t border-[#E4E5E9] hover:bg-[#F5F5F7] cursor-pointer" data-testid={`client-row-${c.id}`}>
                <td className="p-3 font-semibold text-[#0B0D17]">{c.name}</td>
                <td className="p-3 text-[#4A4D57]">{c.mobile || c.email || "—"}</td>
                <td className="p-3">{c.location || "—"}</td>
                <td className="p-3"><span className="inline-block px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-semibold">{c.status}</span></td>
                <td className="p-3 text-xs text-[#747680]">{c.client_since ? new Date(c.client_since).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#747680]">No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="font-display font-black text-xl mb-4">New client</h2>
            <div className="space-y-3">
              {[{ k: "name", ph: "Full name *" }, { k: "mobile", ph: "Mobile" }, { k: "email", ph: "Email" }, { k: "location", ph: "Location" }].map((f) => (
                <input key={f.k} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.ph} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid={`new-client-${f.k}`} />
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={create} className="vp-btn-primary text-sm" data-testid="new-client-save">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
